import { useState, useEffect } from 'react';
import { IconButton, Tooltip, Alert, CircularProgress, Box, Collapse } from '@mui/material';

import {
	Cached as CachedIcon,
	AccessTime as ClockIcon
} from '@mui/icons-material';

import { useLocalization } from 'hooks/useLocalization';
import { useActivityShopUI } from 'hooks/useActivityShopUI';

import LowAvailabilityDialog from 'components/activity-display/activity-content/LowAvailabilityDialog';

import { FormControl, FormControlLabel, FormLabel, RadioGroup, Radio } from '@mui/material';

import { AvailableDatesMonth, ActivityInteractionData, GroupActivityDeparture } from 'interfaces/ActivityShopUIInterfaces';
import CollapsedAlert from 'components/shared/CollapsedAlert';

import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import './style.css';

interface ActivityDatePickerGroupProps {
	activityId: number;
	activityType: 'private' | 'group';
	activityInteractionData: ActivityInteractionData;
	todayDate: Date;
}

function ActivityDatePickerGroup({ activityId, activityType, activityInteractionData, todayDate }: ActivityDatePickerGroupProps) {
	const [showLowAvailabilityDialog, setShowLowAvailabilityDialog] = useState(false);
	const [availabilityErrorType, setAvailabilityErrorType] = useState<'soldOut' | 'insufficientAvailability' | null>(null); // ['soldOut', 'insufficientAvailability'
	const [lowAvailabilityDate, setLowAvailabilityDate] = useState<Date | null>(null);
	const [hasNoDeparturesSelectedMonth, setHasNoDeparturesSelectedMonth] = useState<boolean>(true);
	
	const { updateActivityDate, updateActivityDeparture, getActivityAvailableMonthDates, removeAllGroupMonthData, reloadMonthDates, getTotalPaxQty, paxArr } = useActivityShopUI();
	const { getStr } = useLocalization();
	const selectedDate = activityInteractionData.dateSelected.group;
	const selectedYear = selectedDate?.getFullYear();
	const totalPaxQty = getTotalPaxQty(paxArr);
	
	let selectedDayDepartures: GroupActivityDeparture[] = [];
	
	if (selectedDate) {
		const selectedMonth = selectedDate.getMonth() + 1; //Add 1 to month because getMonth() returns 0-11
		
		//Fetch departures of selected date
		selectedDayDepartures = activityInteractionData.availableDates.group
			.find(year => year.year === selectedYear)?.months
			.find(month => month.month === selectedMonth)?.days
			.find(day => day.dayNum === selectedDate.getDate())?.departures ?? [];
	}
	
	const today = todayDate;
	const todayMonth = today.getMonth() + 1; //Add 1 to month because getMonth() returns 0-11
	const todayYear = today.getFullYear();

	const [selectedMonthData, setSelectedMonthData] = useState<AvailableDatesMonth | null>(null);
	const [currentView, setCurrentView] = useState<{ month: number, year: number }>({ month: todayMonth, year: todayYear });

	const handleDateChange = (newDate: Date | undefined) => {
		if (newDate) {
			const newDateYmd = newDate.toISOString().split('T')[0];

			const soldOutDatesYmd = soldOutDates.map(date => 
				date.toISOString().split('T')[0]
			);

			const insufficientAvailabilityDatesYmd = insufficientAvailabilityDates.map(date => 
				date.toISOString().split('T')[0]
			);

			if (soldOutDatesYmd.includes(newDateYmd)) {
				setAvailabilityErrorType('soldOut');
				setLowAvailabilityDate(newDate);
				setShowLowAvailabilityDialog(true);

			} else if (insufficientAvailabilityDatesYmd.includes(newDateYmd)) {
				setAvailabilityErrorType('insufficientAvailability');
				setLowAvailabilityDate(newDate);
				setShowLowAvailabilityDialog(true);
			} else { //Date is available
				updateActivityDate(
					activityId,
					activityType,
					newDate
				);
			}
		}
	};
	
	function handleMonthChange(newMonth: Date) {
		//Get month and year from new month
		const month = newMonth.getMonth() + 1; //Add 1 to month because getMonth() returns 0-11
		const year = newMonth.getFullYear();
		setCurrentView({ month, year });
		setSelectedMonthData(getActivityAvailableMonthDates(activityId, activityType, month, year));
	}

	//Select current month on load. useEffect() feels a bit hacky though? It's to avoid setting state within render, at setActivitiesInteractionData() within getActivityAvailableMonthDates()
	useEffect(() => {
		//Remove all month data
		removeAllGroupMonthData(activityId);

		//Update to current month
		setSelectedMonthData(getActivityAvailableMonthDates(activityId, activityType, todayMonth, todayYear));
	}, [])

	useEffect(() => {
		// Fetch the latest month data based on the current view
		const newMonthData = getActivityAvailableMonthDates(activityId, activityType, currentView.month, currentView.year);
		setSelectedMonthData(newMonthData);
	}, [activityId, activityType, currentView, activityInteractionData]);

	useEffect(() => {
		//Check if there are no departures at all in current month
		if (selectedMonthData && !selectedMonthData.isLoading) {
			const hasNoDepartures = selectedMonthData.days.every(day => day.departures.length === 0);
			setHasNoDeparturesSelectedMonth(hasNoDepartures);
		}
	}, [selectedMonthData]);
	
	function getDisabledDates(date: Date) {
		// Disable weekends
		if (!selectedMonthData || selectedMonthData.isLoading || activityInteractionData.isAdded) {
			return true;
		}

		//Find day in month data
		const dayData = selectedMonthData.days.find(day => day.dayNum === date.getDate());

		if (!dayData) { //This day has no departures - disable
			return true;
		}

		//Check if all departures are sold out
		/* const allDeparturesSoldOut = dayData.departures.every(departure => departure.groups.every(group => group.occupancy >= group.capacity));
		if (allDeparturesSoldOut) {
			return true;
		} */

		return false;
	}

	const handleReloadClick = () => {
		reloadMonthDates(activityId, currentView.month, currentView.year);
	};

	const handleDepartureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedDeparture: GroupActivityDeparture | undefined = selectedDayDepartures.find(departure => departure.time === event.target.value);

		if (!selectedDeparture) {
			return;
		}

		updateActivityDeparture(
			activityId,
			selectedDeparture.id,
			selectedDeparture.time
		);
	};

	const modifierStyles = {
		soldOut: { backgroundColor: '#ef8d8d', color: '#ffffff' },
		insufficientAvailability: { backgroundColor: '#f7cbcb', color: '#ffffff' },
	};
	
	const soldOutDates = (selectedMonthData && !selectedMonthData.isLoading) ? selectedMonthData.days.reduce((acc: Date[], day) => {
		// Check if all departures for the day are sold out
		const allDeparturesSoldOut = day.departures.every(departure => 
			departure.groups.every(group => group.occupancy >= group.capacity)
		);

		if (allDeparturesSoldOut) {
			const soldOutDate = new Date(currentView.year, currentView.month - 1, day.dayNum);
			acc.push(soldOutDate);
		}

		return acc;
	}, []) : [];

	const insufficientAvailabilityDates = (selectedMonthData && !selectedMonthData.isLoading) ? selectedMonthData.days.reduce((acc: Date[], day) => {
		// Aggregate total availability for the day across all departures
		const totalDayAvailability = day.departures.reduce((total, departure) => {
			const departureAvailability = departure.groups.reduce((avail, group) => {
				// Calculate available spots in the group
				const groupAvailability = group.capacity - group.occupancy;
				return avail + groupAvailability;
			}, 0);
			return total + departureAvailability;
		}, 0);
	
		// Determine if any single departure can accommodate all passengers
		const isInsufficient = day.departures.some(departure => {
			const departureTotalAvailability = departure.groups.reduce((avail, group) => {
				// Calculate available spots in the group
				const groupAvailability = group.capacity - group.occupancy;
				return avail + groupAvailability;
			}, 0);
			// Assuming 'totalPaxQty' is the number of spots you're checking for
			// This checks if no single departure can accommodate all passengers
			return departureTotalAvailability > 0 && departureTotalAvailability < totalPaxQty;
		});
	
		if (totalDayAvailability > 0 && isInsufficient) {
			const insufficientDate = new Date(currentView.year, currentView.month - 1, day.dayNum);
			acc.push(insufficientDate);
		}
	
		return acc;
	}, []) : [];
	

	const modifiers = {
		insufficientAvailability: (date: Date) => insufficientAvailabilityDates.some(insufficientAvailabilityDate => date.toDateString() === insufficientAvailabilityDate.toDateString()),
		soldOut: (date: Date) => soldOutDates.some(soldOutDate => date.toDateString() === soldOutDate.toDateString()),
	};

	return (
		<>
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', marginBottom: '30px' }}>
				<div className="activity-calendar-wrapper">
					<DayPicker
						mode="single"
						selected={selectedDate}
						onSelect={handleDateChange}
						defaultMonth={selectedDate}
						weekStartsOn={1}
						onMonthChange={handleMonthChange}
						fromMonth={todayDate}
						disabled={getDisabledDates}
						modifiers={modifiers}
						modifiersStyles={modifierStyles}
					/>
					{
						<LowAvailabilityDialog
							isOpen={showLowAvailabilityDialog}
							onClose={() => {setShowLowAvailabilityDialog(false)}}
							errorType={availabilityErrorType}
							errorDate={lowAvailabilityDate}
							activityId={activityId}
							selectedMonthData={selectedMonthData}
						/>
					}
				{(!selectedMonthData || selectedMonthData.isLoading) && (
					<div className="activity-calendar-loading-indicator">
						<CircularProgress size={50} />
					</div>
				)}
				</div>
				{
					selectedDayDepartures && selectedDayDepartures.length > 1 && activityInteractionData ? (
						<FormControl
							disabled={activityInteractionData.isAdded}
						>
							<FormLabel id="demo-radio-buttons-group-label"></FormLabel>
							<RadioGroup
								onChange={handleDepartureChange}
								value={activityInteractionData.timeSelected.group}
								aria-labelledby="demo-radio-buttons-group-label"
								name="radio-buttons-group"
							>
								{selectedDayDepartures.map(departure => (
									<FormControlLabel key={departure.time} value={departure.time} control={<Radio />} label={
										<Box display="flex" alignItems="center" justifyContent="center">
											<ClockIcon sx={{ mr: 1 }} />
											{departure.time}
										</Box>
									} />
								))}
							</RadioGroup>
						</FormControl>
					) : (
						<Collapse in={!!selectedDayDepartures && selectedDayDepartures.length === 1 && !!activityInteractionData}>
							<Box display="flex" alignItems="center" justifyContent="center" gap={1}>
								<span style={{ fontWeight: 'bold', marginRight: '20px' }}>{getStr('Start_time')}</span>
								<ClockIcon />
								{selectedDayDepartures && selectedDayDepartures.length > 0 && (
									<>
										{selectedDayDepartures[0].time}
									</>
								)}
								
							</Box>
						</Collapse>
					)
				}
			</Box>
			{
				(selectedMonthData && selectedMonthData.fetchError) && (
					<p>
						<Alert
							severity="error"
							action={
								<IconButton
									aria-label={getStr('Reload')}
									size="small"
									onClick={handleReloadClick}
								>
									<Tooltip title={getStr('Reload')}>
										<CachedIcon fontSize="inherit" />
									</Tooltip>
								</IconButton>
							}
						>
							{selectedMonthData.fetchError}
						</Alert>
					</p>
				)
			}
			<CollapsedAlert isVisible={!!selectedMonthData && selectedMonthData.isLoading && !activityInteractionData.isAdded}>
				{getStr('Loading_month')}
			</CollapsedAlert>
			<CollapsedAlert isVisible={!selectedDate && (!selectedMonthData?.isLoading) && (!hasNoDeparturesSelectedMonth || !selectedMonthData)} severity="error">
				{getStr('Please_select_a_date')}
			</CollapsedAlert>
			<CollapsedAlert isVisible={!activityInteractionData.isAdded && !!selectedMonthData && !selectedMonthData.isLoading && hasNoDeparturesSelectedMonth} severity="error">
				{getStr('No_available_dates_in_this_month')}
			</CollapsedAlert>
		</>
	);
}

export default ActivityDatePickerGroup;